import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import React, { useMemo } from 'react';
import { Navigate } from 'react-router';
import { useRolesMatch } from '../index.js';
/**
 * Allows you to protect content based on the user role.
 *
 * Intended to be used together with `react-router` v6 only.
 * For v5 code please use `PrivateRoute` component.
 * @param props - props of the component
 * @param props.children - private content
 * @param props.roles - roles that are required to access the content (can be single role, array, or predicate function created with and/or/not)
 * @param props.redirectPath - the path to which the user will be redirected if he has no access
 * @param props.noAccessComponent - component to be used when the user doesn't have an access to content
 * @param props.noAccessElement - element to be used when the user doesn't have an access to content
 * @returns JSX.Element
 */
const PrivateContent = ({ children, roles, ...noAccessProps }) => {
    const matches = useRolesMatch({ roles });
    const noAccessElement = useMemo(() => {
        if (matches) {
            return null;
        }
        if (noAccessProps.noAccessComponent) {
            const { noAccessComponent: NoAccessComponent } = noAccessProps;
            return _jsx(NoAccessComponent, {});
        }
        if (noAccessProps.redirectPath !== undefined) {
            const { redirectPath } = noAccessProps;
            return _jsx(Navigate, { to: redirectPath, replace: true });
        }
        return noAccessProps.noAccessElement ?? null;
    }, [matches, noAccessProps]);
    if (!matches) {
        return _jsx(_Fragment, { children: noAccessElement });
    }
    return _jsx(_Fragment, { children: children });
};
export default React.memo(PrivateContent);
